import { CrmPipelineStageCode } from './crmPipeline';

/** Mutually exclusive due-date buckets returned by the backend dashboard endpoint. */
export type CrmDashboardDueBucket = 'completed' | 'none' | 'overdue' | 'today' | 'soon' | 'later';

export interface CrmDashboardStage {
  stageCode: CrmPipelineStageCode | string;
  stageName: string;
  isOpenStage: boolean;
  customerCount: number;
}

export interface CrmDashboardTaskItem {
  id: number;
  customerId: number;
  customerName: string;
  title: string;
  dueAtUtc: string | null;
  priorityCode: string;
  priorityName: string;
  dueBucket: CrmDashboardDueBucket | string;
  assignedUserId: string | null;
  assignedUserName: string | null;
}

export interface CrmDashboardCustomerItem {
  id: number;
  name: string;
  contacts: string;
  stageCode: string;
  stageName: string;
  nextContactAtUtc: string | null;
  pendingTaskCount: number;
  assignedUserId: string | null;
  assignedUserName: string | null;
}

export interface CrmDashboardEventItem {
  id: number;
  eventName: string;
  eventVenue: string;
  eventDate: string;
  guestCount: number;
  orderStatusId: number;
  orderStatus: string;
  totalPrice: number;
  customerId: number | null;
  customerName: string | null;
}

export interface CrmDashboard {
  generatedAtUtc: string;
  todayStartUtc: string;
  todayEndUtc: string;
  upcomingTasksEndUtc: string;
  upcomingEventsEndUtc: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  activeCustomerCount: number;
  totalCustomerCount: number;
  pipeline: CrmDashboardStage[];
  closedCustomerCount: number;
  wonCustomerCount: number;
  winRatePercent: number | null;
  customersWithCompletedEventsCount: number;
  repeatCustomerCount: number;
  repeatCustomerRatePercent: number | null;
  overdueContactCount: number;
  customersWithoutNextActionCount: number;
  pendingTaskCount: number;
  overdueTaskCount: number;
  tasksDueTodayCount: number;
  tasksDueNext7DaysCount: number;
  upcomingEventCount: number;
  overdueTasks: CrmDashboardTaskItem[];
  upcomingTasks: CrmDashboardTaskItem[];
  overdueContacts: CrmDashboardCustomerItem[];
  customersWithoutNextAction: CrmDashboardCustomerItem[];
  upcomingEvents: CrmDashboardEventItem[];
}

const DUE_BUCKET_LABELS: Record<string, string> = {
  completed: 'Виконано',
  none: 'Без дедлайну',
  overdue: 'Прострочено',
  today: 'Сьогодні',
  soon: 'Найближчі 7 днів',
  later: 'Пізніше',
};

/** Ukrainian label for a due bucket; unknown codes are returned unchanged. */
export const dueBucketLabel = (bucket?: string | null): string =>
  (bucket && DUE_BUCKET_LABELS[bucket]) || bucket || '';

/** Deep link that opens a customer card directly on the CRM customers page. */
export const customerLink = (customerId: number): string => `/management/customers?customerId=${customerId}`;

/** Event orders page link. The page has no per-order route yet, so the list is opened. */
export const eventOrdersLink = (): string => '/management/event-orders';

/** Formats a UTC ISO timestamp as a local date+time string, or an em dash when missing. */
export const formatDateTime = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString('uk-UA') : '—';

/** Formats a UTC ISO timestamp as a local date string, or an em dash when missing. */
export const formatDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('uk-UA') : '—';

/** Formats UAH amounts the same way as the rest of the CRM pages. */
export const formatMoney = (value?: number | null): string =>
  `${(value || 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;

/**
 * Whole-day difference between a deadline and `now`, rounded towards zero on the day boundary:
 * negative values are days overdue, 0 means the deadline is today (local time).
 */
export const daysUntil = (iso: string | null | undefined, now: Date = new Date()): number | null => {
  if (!iso) return null;
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return null;
  const dueDay = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
  const nowDay = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((dueDay - nowDay) / 86400000);
};

/** Human readable relative deadline in Ukrainian, e.g. "Сьогодні" / "Прострочено на 3 дн.". */
export const relativeDueLabel = (iso: string | null | undefined, now: Date = new Date()): string => {
  const days = daysUntil(iso, now);
  if (days === null) return 'Без дедлайну';
  if (days === 0) return 'Сьогодні';
  if (days === 1) return 'Завтра';
  if (days === -1) return 'Прострочено на 1 дн.';
  if (days < 0) return `Прострочено на ${Math.abs(days)} дн.`;
  return `Через ${days} дн.`;
};

/** Relative share (0..100) of a stage inside the pipeline, used for the CSS bar chart. */
export const stageSharePercent = (count: number, maxCount: number): number => {
  if (maxCount <= 0 || count <= 0) return 0;
  return Math.max(4, Math.round((count / maxCount) * 100));
};